import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import * as _ from 'lodash-es';
import { JsonObjectFactoryService } from '../../shared/_services/json-object-factory.service';

@Injectable({
  providedIn: 'root'
})
export class FirDiagnosticsService {
  constructor(private jsonObjectFactoryService: JsonObjectFactoryService) {}

  saveFIRDiagnosticsAction(
    baseUrl: string,
    data: any,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    if (!data?.rowData) {
      return of({ callSuccess: '0', errorMessage: 'Row data is required.' });
    }

    const rowData = data.rowData;
    const jsonSettings = [{
      COMBINATION_KEY: rowData.COMBINATION_KEY,
      TP_KEY: rowData.TP_KEY == null ? 0 : rowData.TP_KEY
    }];

    // Legacy parity (sub-part-f.service.ts pattern): when validation fails we
    // must restore the previously persisted value of the edited cell so the
    // grid stops treating it as "saved" — otherwise the user has to re-edit
    // the cell to trigger another save attempt.
    const colId: string | undefined = data?.col_id;
    const oldEditedValue = colId
      ? (data?.oldRowData == null ? data?.oldValue : data.oldRowData[colId])
      : undefined;
    const currentEditedValue = colId ? rowData[colId] : undefined;

    // No-op edits (focus/blur without a change) shouldn't show snackbars.
    if (colId && currentEditedValue === oldEditedValue) {
      return of({ callSuccess: '1', skipped: true });
    }

    const hasComments = typeof rowData.COMMENTS === 'string' && rowData.COMMENTS.trim() !== '';
    const actionIsValid = _.isString(rowData.ACTION) && rowData.ACTION.includes('-');

    // Legacy AngularJS parity: for the EQUITY grid the user must pick an Action
    // before persisting Comments. The original factory.saveFIRDiagnosticsAction
    // rejected the save when the comment was filled in but ACTION was empty;
    // without this guard the empty-jsonObj request silently "succeeds" and the
    // user sees a green snackbar even though nothing was saved.
    if (rowData.GRID_TYPE === 'EQUITY' && !actionIsValid && hasComments) {
      // Restore the value of the edited cell (typically COMMENTS) so the grid
      // mirrors the rejected state and the user has to re-enter it AFTER
      // selecting an Action, matching the AngularJS factory behaviour.
      if (colId) {
        rowData[colId] = data?.oldValue !== undefined
          ? data.oldValue
          : data?.oldRowData?.[colId];
      }
      return of({
        callSuccess: '0',
        errorMessage: 'Please select an Action before saving Comments.'
      });
    }

    const jsonObj: any[] = [];
    if (rowData.GRID_TYPE === 'EQUITY' || rowData.GRID_TYPE === 'LOAN') {
      if (rowData.GRID_TYPE === 'EQUITY' && actionIsValid) {
        jsonObj.push({
          DIAG_TYPE: rowData.DIAG_TYPE,
          ACTION_ID: parseInt(String(rowData.ACTION).split('-')[0], 10),
          COMMENTS: rowData.COMMENTS ?? null
        });
      } else if (rowData.GRID_TYPE !== 'EQUITY') {
        jsonObj.push({
          DIAG_TYPE: rowData.DIAG_TYPE,
          ACTION_ID: null,
          COMMENTS: rowData.COMMENTS ?? null
        });
      }
    }

    const params = _.merge(
      {},
      globalService?.globalParams || globalService || {},
      globalService?.globalFilterValues || {},
      filterParams,
      globalService?.isIssueIDEnabled
        ? globalService.isIssueIDEnabled(globalService?.globalParams?.tax_year, globalService?.globalParams?.scenario)
        : {},
      {
        jsonObj: JSON.stringify(jsonObj),
        jsonSetting: JSON.stringify(jsonSettings),
        checkLockedbj: JSON.stringify([{
          combination_key: rowData.COMBINATION_KEY,
          scenario_key: filterParams?.scenario,
          tax_year: filterParams?.tax_year
        }])
      }
    );

    return this.jsonObjectFactoryService.saveJSON6(
      `${baseUrl}/saveJsonObject?action_code=7edm8n`,
      params,
      globalService,
      filterParams
    );
  }
}
